import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/app/Logo";
import DemoScanner from "@/components/app/DemoScanner";
import { APP_VERSION } from "@/lib/version";
import {
  Camera,
  MagnifyingGlass,
  CheckCircle,
  XCircle,
  Tag,
  ChartLineUp,
  ClipboardText,
  Package,
  TrendUp,
  Lightning,
  ArrowRight,
  ShieldCheck,
  CurrencyDollar,
  Storefront,
} from "@phosphor-icons/react";

function Marquee() {
  const items = [
    "GRAB", "OR", "SKIP", "//",
    "NIKE", "CARHARTT", "JORDAN", "FREE PEOPLE", "//",
    "SCAN", "FLIP", "PROFIT", "//",
    "GRAB", "OR", "SKIP", "//",
    "SOURCE", "SCOUT", "SELL", "REPEAT", "//",
  ];
  return (
    <div className="bg-black text-white py-4 border-y-4 border-black overflow-hidden">
      <div className="flex items-center gap-8 whitespace-nowrap animate-marquee">
        {[...items, ...items].map((item, i) => (
          <span
            key={i}
            className={`font-heading font-black text-2xl sm:text-3xl uppercase tracking-widest ${
              item === "GRAB" ? "text-[#00E676]" : item === "SKIP" ? "text-[#FF1744]" : item === "//" ? "text-neutral-600" : ""
            }`}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function VerdictDemo({ type, brand, title, price, className = "" }) {
  const isGrab = type === "GRAB";
  return (
    <div
      className={`border-4 border-black bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] overflow-hidden ${className}`}
      data-testid={`demo-verdict-${type.toLowerCase()}`}
    >
      <div className={`px-4 py-3 border-b-4 border-black ${isGrab ? "bg-[#00E676]" : "bg-[#FF1744]"}`}>
        <div className={`font-heading text-3xl font-black uppercase tracking-tighter ${isGrab ? "text-black" : "text-white"}`}>
          {type}
        </div>
      </div>
      <div className="p-4">
        <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">{brand}</div>
        <div className="font-heading text-sm font-bold tracking-tight mt-1">{title}</div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t-2 border-black">
          <div className="font-heading text-xl font-bold">{price}</div>
          {isGrab ? <CheckCircle size={20} weight="bold" className="text-[#00E676]" /> : <XCircle size={20} weight="bold" className="text-[#FF1744]" />}
        </div>
      </div>
    </div>
  );
}

function StepCard({ number, title, description, icon: Icon }) {
  return (
    <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-6 flex flex-col" data-testid={`step-${number}`}>
      <div className="flex items-start justify-between mb-4">
        <span className="font-heading text-7xl font-black text-neutral-200 leading-none">{number}</span>
        <Icon size={36} weight="bold" className="text-black" />
      </div>
      <h3 className="font-heading text-xl font-bold tracking-tight uppercase mt-auto">{title}</h3>
      <p className="font-body text-sm text-neutral-600 mt-2">{description}</p>
    </div>
  );
}

function FeatureCard({ title, description, icon: Icon, bgColor = "bg-white" }) {
  return (
    <div className={`${bgColor} border-4 border-black p-6 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]`}>
      <Icon size={28} weight="bold" className="mb-3" />
      <h3 className="font-heading text-lg font-bold tracking-tight uppercase">{title}</h3>
      <p className="font-body text-sm text-neutral-600 mt-2 leading-relaxed">{description}</p>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const ctaLink = user ? "/scan" : "/register";
  const ctaText = user ? "Open App" : "Start Scanning Free";

  return (
    <div className="min-h-screen bg-[#F4F4F0]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#F4F4F0] border-b-4 border-black px-6 py-4 flex items-center justify-between" data-testid="landing-nav">
        <Logo size={36} showText className="text-xl" />
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/scan" className="bg-black text-white px-5 py-2.5 font-heading font-bold text-sm uppercase tracking-wide border-2 border-black shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all" data-testid="nav-open-app">
              Open App
            </Link>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2.5 font-heading font-bold text-sm uppercase tracking-wide hover:underline underline-offset-4" data-testid="nav-login">Sign In</Link>
              <Link to="/register" className="bg-black text-white px-5 py-2.5 font-heading font-bold text-sm uppercase tracking-wide border-2 border-black shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all" data-testid="nav-signup">Sign Up</Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-3 py-1 bg-white border-2 border-black font-body text-xs font-bold shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] uppercase tracking-[0.2em] mb-6">
              AI-Powered Resale Scouting
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none uppercase">
              Know What to{" "}
              <span className="text-[#00E676]">Grab</span>
              <br />
              Before You
              <br />
              Buy
            </h1>
            <p className="font-body text-base md:text-lg text-neutral-600 mt-6 max-w-md leading-relaxed">
              Snap a photo while you&rsquo;re sourcing. Get an instant verdict with pricing, comps, and the ideal cost to acquire. Stop guessing, start profiting.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to={ctaLink}
                className="bg-black text-white px-8 py-4 font-heading text-lg font-bold uppercase tracking-widest border-4 border-black shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] hover:translate-x-[4px] hover:translate-y-[4px] hover:shadow-none active:translate-x-[8px] active:translate-y-[8px] transition-all flex items-center gap-3"
                data-testid="hero-cta"
              >
                {ctaText}
                <ArrowRight size={22} weight="bold" />
              </Link>
            </div>
            <p className="font-body text-sm text-neutral-500 mt-4">
              Free to use &middot; No credit card required
            </p>
          </div>
          <div className="relative flex justify-center md:justify-end">
            <VerdictDemo type="GRAB" brand="Nike" title="Nike Vintage Swoosh Hoodie Gray L" price="$25-$35" className="w-64 transform -rotate-2" />
            <VerdictDemo type="SKIP" brand="Cato" title="Cato Floral Print Blouse XL" price="$3-$5" className="w-56 absolute -bottom-8 -left-4 md:left-auto md:-right-4 transform rotate-3" />
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee />

      {/* How It Works */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight uppercase mb-4 text-center">
            How It Works
          </h2>
          <p className="font-body text-base text-neutral-500 text-center mb-12 max-w-lg mx-auto">
            Three steps from sourcing to selling. No guesswork, no research rabbit holes.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <StepCard
              number="1"
              title="Snap a Photo"
              description="Photograph any item while you're sourcing. Multiple angles work even better — the AI analyzes every detail."
              icon={Camera}
            />
            <StepCard
              number="2"
              title="AI Scans the Market"
              description="AI identifies the brand, checks real-time sold comps, calculates sell-through rate, and estimates pricing in seconds."
              icon={MagnifyingGlass}
            />
            <StepCard
              number="3"
              title="Grab or Skip"
              description="Get a color-coded verdict: Green means grab it, Yellow means caution, Red means walk away. See exactly what it'll sell for."
              icon={Lightning}
            />
          </div>
        </div>
      </section>

      {/* Live Demo */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 bg-black border-y-4 border-black" data-testid="demo-section">
        <div className="max-w-lg mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight uppercase mb-4 text-center text-white">
            Try It Now
          </h2>
          <p className="font-body text-base text-neutral-400 text-center mb-8">
            Upload a photo of any resale item. Get an instant verdict — no account needed.
          </p>
          <DemoScanner />
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 bg-white border-y-4 border-black" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight uppercase mb-4 text-center">
            Built for Resellers
          </h2>
          <p className="font-body text-base text-neutral-500 text-center mb-12 max-w-lg mx-auto">
            Everything you need to make faster, smarter sourcing decisions and maximize profit on every item.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={Camera}
              title="Instant AI Verdicts"
              description="Upload photos and get brand identification, condition assessment, pricing data, sold comps, demand level, and a clear Grab/Caution/Skip verdict."
              bgColor="bg-[#00E676]/10"
            />
            <FeatureCard
              icon={Tag}
              title="Smart Brand Rules"
              description="Nike, Carhartt, Jordan — always grab. Built-in brand intelligence with NWT overrides and customizable rules for your sourcing strategy."
              bgColor="bg-white"
            />
            <FeatureCard
              icon={Storefront}
              title="eBay Integration"
              description="List directly to eBay with AI-generated titles, auto-filled aspects, category detection, and promoted listing campaigns. Edit live listings in-app."
              bgColor="bg-[#FFEA00]/10"
            />
            <FeatureCard
              icon={Package}
              title="Haul Tracker"
              description="Log every sourcing trip with items grabbed, receipt photos, and per-item cost. Track your running P&L across all hauls automatically."
              bgColor="bg-white"
            />
            <FeatureCard
              icon={ShieldCheck}
              title="Counterfeit Detection"
              description="AI checks tag quality, stitching patterns, and brand markers to flag potential fakes before you buy. Avoid costly mistakes at the source."
              bgColor="bg-[#F4F4F0]"
            />
            <FeatureCard
              icon={TrendUp}
              title="Real-Time Market Data"
              description="Live sell-through rates, active competition counts, median sold prices, and seasonal demand data — all from eBay's marketplace in real time."
              bgColor="bg-white"
            />
          </div>
        </div>
      </section>

      {/* Pricing Intel Breakdown */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="pricing-intel-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight uppercase mb-4 text-center">
            Every Number You Need
          </h2>
          <p className="font-body text-base text-neutral-500 text-center mb-12 max-w-lg mx-auto">
            Each scan delivers a complete pricing breakdown so you can decide in seconds, not minutes.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: CurrencyDollar, label: "Suggested List Price", desc: "Based on real median sold prices, not guesswork" },
              { icon: Tag, label: "Acquisition Cost Range", desc: "The ideal price to pay — 10-15% of list price" },
              { icon: ChartLineUp, label: "Profit Range", desc: "Your realistic profit window after fees and shipping" },
              { icon: TrendUp, label: "Sell-Through Rate", desc: "Percentage of listings that actually sell — the #1 demand signal" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 border-4 border-black bg-white p-5 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
                <item.icon size={24} weight="bold" className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading text-sm font-bold uppercase tracking-tight">{item.label}</div>
                  <div className="font-body text-sm text-neutral-600 mt-1">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24 bg-white border-y-4 border-black" data-testid="social-proof-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight uppercase mb-12">
            The 5-Second Decision
          </h2>
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <div className="border-4 border-black bg-white p-6 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
              <div className="font-heading text-5xl font-black text-[#00E676]">5s</div>
              <div className="font-body text-sm text-neutral-600 mt-2">Average verdict time</div>
            </div>
            <div className="border-4 border-black bg-white p-6 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
              <div className="font-heading text-5xl font-black">42%</div>
              <div className="font-body text-sm text-neutral-600 mt-2">Avg sell-through rate on grabs</div>
            </div>
            <div className="border-4 border-black bg-white p-6 shadow-[4px_4px_0px_0px_rgba(10,10,10,1)]">
              <div className="font-heading text-5xl font-black text-[#FF1744]">0</div>
              <div className="font-body text-sm text-neutral-600 mt-2">Bad flips when you listen</div>
            </div>
          </div>
          <div className="border-4 border-black bg-black text-white p-8 shadow-[8px_8px_0px_0px_rgba(10,10,10,0.3)]">
            <div className="font-heading text-6xl font-black leading-none mb-4">&ldquo;</div>
            <p className="font-body text-base md:text-lg leading-relaxed italic">
              I used to grab everything that looked decent and end up with a closet full of deadstock. Now I scan first and only grab winners. My sell-through rate doubled.
            </p>
            <div className="font-body text-sm text-neutral-400 mt-4 uppercase tracking-[0.1em]">
              — Every reseller after using GrabOrSkip
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white border-t-4 border-black px-6 md:px-12 lg:px-16 py-16 md:py-24" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none uppercase mb-6">
            Stop Guessing.<br />Start Scanning.
          </h2>
          <p className="font-body text-base md:text-lg text-neutral-400 mb-10 max-w-lg mx-auto">
            Every minute sourcing is money. Know what to grab before your competition does.
          </p>
          <Link
            to={ctaLink}
            className="inline-flex items-center gap-3 bg-[#00E676] text-black px-10 py-5 font-heading text-xl font-black uppercase tracking-widest border-4 border-white shadow-[8px_8px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-[4px] hover:translate-y-[4px] hover:shadow-none active:translate-x-[8px] active:translate-y-[8px] transition-all"
            data-testid="footer-cta"
          >
            {ctaText}
            <ArrowRight size={24} weight="bold" />
          </Link>
          <p className="font-body text-sm text-neutral-500 mt-6">
            Free to use &middot; Works on any phone &middot; No app download needed
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white border-t-2 border-neutral-800 px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={28} showText darkBg className="text-sm" />
          <div className="font-body text-xs text-neutral-500">
            &copy; {new Date().getFullYear()} GrabOrSkip. Built for resellers.
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="font-body text-xs text-neutral-500 hover:text-neutral-300">Privacy</Link>
            <span className="font-body text-[9px] text-neutral-600" data-testid="landing-version">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
